import React from 'react';
import { Check, Award, Leaf, Clock } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const AboutSection = () => {
  const { t } = useTranslation();
  
  const features = [
    {
      icon: Award,
      title: t('masterChefs'),
      description: t('masterChefsDesc')
    },
    {
      icon: Leaf,
      title: t('freshIngredients'),
      description: t('freshIngredientsDesc')
    },
    {
      icon: Clock,
      title: t('fastDelivery'),
      description: t('fastDeliveryDesc')
    },
    {
      icon: Check,
      title: t('qualityAssured'),
      description: t('qualityAssuredDesc')
    }
  ];

  return (
    <section id="about" className="py-24 bg-black text-white relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          {/* Left Content */}
          <div>
            <span className="inline-block px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-sm font-medium tracking-[0.2em] uppercase mb-8">
              {t('ourStory')}
            </span>
            <h2 className="text-5xl md:text-6xl font-bold mb-8 leading-tight">
              {t('aboutTitle')}
              <br />
              <span className="bg-gradient-to-r from-white via-gray-300 to-gray-500 bg-clip-text text-transparent">
                {t('aboutSubtitle')}
              </span>
            </h2>
            <p className="text-gray-300 text-xl mb-6 leading-relaxed">
              {t('aboutText1')}
            </p>
            <p className="text-gray-300 text-xl mb-10 leading-relaxed">
              {t('aboutText2')}
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-black" />
                </div>
                <span className="text-gray-300">{t('sustainableSeafood')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-black" />
                </div>
                <span className="text-gray-300">{t('traditionalTechniques')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-black" />
                </div>
                <span className="text-gray-300">{t('madeToOrder')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-black" />
                </div>
                <span className="text-gray-300">{t('premiumIngredients')}</span>
              </div>
            </div>
          </div>

          {/* Right Image Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <img
                src="https://customer-assets.emergentagent.com/job_mikado-eats/artifacts/a6u2opq7_Banzai.JPG"
                alt="Banzay Maki"
                className="rounded-2xl w-full h-64 object-cover shadow-xl hover:scale-105 transition-transform duration-500"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_mikado-eats/artifacts/4yc8j59t_Avokado%20saki%20maki.JPG"
                alt="Sake-Avocado Maki"
                className="rounded-2xl w-full h-48 object-cover shadow-xl hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="space-y-4 mt-8">
              <img
                src="https://customer-assets.emergentagent.com/job_mikado-eats/artifacts/c1wzex3z_Ebi%20Tempura.JPG"
                alt="Ebi Tempura"
                className="rounded-2xl w-full h-48 object-cover shadow-xl hover:scale-105 transition-transform duration-500"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_mikado-eats/artifacts/3x1i22q4_Lasa%20tartara%20poke%20%281%29.JPG"
                alt="Salmon Tartare Poke"
                className="rounded-2xl w-full h-64 object-cover shadow-xl hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-20">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="group p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:bg-white/10 transition-all duration-300 hover:scale-105"
              >
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Icon className="w-7 h-7 text-black" />
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;